import {
  AbsoluteFill,
  Audio,
  continueRender,
  delayRender,
  OffthreadVideo,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { BrollCaptionRendererProps, EmojiMap } from '../../types';
import { getCurrentEffect } from '../../utils/helper';
import CaptionLayerContainer from './CaptionLayerContainer';
import { BrollLayerContainer } from './BrollLayerContainer';
import { BrollEffectVideo } from './BrollEffectVideo';
import { loadAllFonts } from '../../utils/loadFont';
import { FALLBACK_VIDEO_MESSAGE } from '../../utils';
loadAllFonts();

export const BrollAndCaptionRenderer: React.FC<BrollCaptionRendererProps> = React.memo(
  ({
    src,
    videoMetadata,
    brolls = [],
    captions = [],
    isRender,
    theme,
    backgroundMusicData,
    shouldShowWatermark,
    cdnBaseUrlPath,
  }) => {
    const frame = useCurrentFrame();
    const { fps, width } = useVideoConfig();

    const { videoPosition } = theme.layout;

    const memoizedBrolls = useMemo(() => brolls, [brolls]);

    const transitionBrolls = useMemo(
      () => brolls.filter((item) => item.transitionEffect !== 'none'),
      [brolls]
    );

    const videoTransition = useMemo(
      () =>
        getCurrentEffect(
          brolls,
          videoMetadata,
          frame,
          'transitionEffect',
          isRender,
          cdnBaseUrlPath
        ),
      [brolls, videoMetadata, frame, cdnBaseUrlPath]
    );

    const brollTransition = useMemo(
      () =>
        getCurrentEffect(
          brolls,
          videoMetadata,
          frame,
          'brollEffect',
          isRender,
          cdnBaseUrlPath
        ),
      [brolls, videoMetadata, frame, isRender, cdnBaseUrlPath]
    );

    const imageTransition = useMemo(
      () =>
        getCurrentEffect(
          brolls,
          videoMetadata,
          frame,
          'imageEffect',
          isRender,
          cdnBaseUrlPath
        ),
      [brolls, videoMetadata, frame, isRender, cdnBaseUrlPath]
    );

    const handleError = useCallback(() => {}, []);

    const [error, setError] = useState(false);
    const autoPlayError = useCallback(() => {
      // setError(true);
    }, []);

    const desiredFontSize = Math.round((width / 1920) * 100);
    const [handle] = useState(() => delayRender('Loading Lottie animation'));
    const [emojiMap, setEmojiMap] = useState<EmojiMap>(new Map());

    useEffect(() => {
      if (!captions?.length) {
        continueRender(handle);
        return;
      }

      const fetchAnimations = async () => {
        try {
          const updatedMap = new Map();

          await Promise.all(
            captions.map(async (element) => {
              if (element.emoji) {
                try {
                  const emojiCode = element.emoji.codePointAt(0)?.toString(16);
                  const response = await fetch(
                    `https://fonts.gstatic.com/s/e/notoemoji/latest/${emojiCode}/lottie.json`
                  );
                  const json = await response.json();

                  updatedMap.set(emojiCode, json);
                } catch (error) {
                  console.error('Failed to fetch animation data for emoji:', element.emoji, error);
                }
              }
            })
          );

          setEmojiMap(updatedMap);
        } catch (error) {
          console.error('Error fetching animations:', error);
        } finally {
          continueRender(handle);
        }
      };

      fetchAnimations();
    }, [handle, captions]);

    // Helper to find active split broll for current frame
    const activeSplitBroll = brolls.find((broll) => {
      const layoutValue = String(broll.brollLayout);
      const isSplit = layoutValue === 'split-50-50' || layoutValue === 'split-65-35';
      const startFrame = broll.startTime * fps;
      const endFrame = broll.endTime * fps;
      return isSplit && frame >= startFrame && frame < endFrame;
    });
    const activeSplitBrollIndex = brolls.findIndex(broll => broll === activeSplitBroll);

    return (
      <AbsoluteFill>
        {/* Main Video Layer - always rendered once */}
        {src && (
          <AbsoluteFill>
            <OffthreadVideo
              key={videoMetadata?.id}
              style={{
                objectFit: 'cover',
                objectPosition: activeSplitBroll
                  ? `${activeSplitBroll.mainPositionX ?? 50}% ${activeSplitBroll.mainPositionY ?? 50}%`
                  : `${videoPosition.x}% ${videoPosition.y}%`,
                width: '100%',
                height: activeSplitBroll
                  ? activeSplitBroll.brollLayout === 'split-50-50' ? '50%' : '65%'
                  : 'inherit',
                transform: !activeSplitBroll ? videoTransition.transform : undefined,
              }}
              src={src}
              pauseWhenBuffering={true}
              onError={handleError}
              onAutoPlayError={autoPlayError}
            />
            {/* Split Broll Overlay Layer (if active) */}
            {activeSplitBroll && (
              <div
                style={{
                  position: 'absolute',
                  top: activeSplitBroll.brollLayout === 'split-50-50' ? '50%' : '65%',
                  left: 0,
                  width: '100%',
                  height: activeSplitBroll.brollLayout === 'split-50-50' ? '50%' : '35%',
                  overflow: 'hidden',
                  background: '#000',
                }}
              >
                {activeSplitBroll.brollType === 'video' ? (
                  <>
                    <OffthreadVideo
                      key={`broll-split-video-${videoMetadata?.id}-${activeSplitBrollIndex}`}
                      muted
                      style={{
                        objectFit: 'cover',
                        objectPosition: `${Math.max(0, Math.min(100, activeSplitBroll.brollOverlay?.positionX ?? 50))}% ${Math.max(0, Math.min(100, activeSplitBroll.brollOverlay?.positionY ?? 50))}%`,
                        width: '100%',
                        height: '100%',
                      }}
                      src={activeSplitBroll.brollUrl ?? ''}
                      pauseWhenBuffering={true}
                      onError={handleError}
                    />
                    <BrollEffectVideo data={brollTransition} id={`${videoMetadata.id}-${activeSplitBrollIndex}`} />
                  </>
                ) : (
                  <>
                    <img
                      key={`img-split-${videoMetadata?.id}-${activeSplitBrollIndex}`}
                      style={{
                        objectFit: 'cover',
                        objectPosition: `${Math.max(0, Math.min(100, activeSplitBroll.brollOverlay?.positionX ?? 50))}% ${Math.max(0, Math.min(100, activeSplitBroll.brollOverlay?.positionY ?? 50))}%`,
                        width: '100%',
                        height: '100%',
                      }}
                      src={activeSplitBroll.brollUrl}
                      alt="B-roll"
                    />
                    <BrollEffectVideo data={imageTransition} id={`${videoMetadata.id}-${activeSplitBrollIndex}`} />
                  </>
                )}
              </div>
            )}
          </AbsoluteFill>
        )}

        {/* Broll Main Video Effect Layer */}
        {transitionBrolls?.map((item, index) => {
          const startFrame = item?.startTime * fps;
          const durationInFrames = (item.endTime - item.startTime) * fps;
          if (durationInFrames > 0) {
            return (
              <Sequence
                key={index}
                from={startFrame}
                premountFor={300}
                durationInFrames={durationInFrames}
              >
                <BrollEffectVideo data={videoTransition} id={`${videoMetadata.id}-abcd`} />
              </Sequence>
            );
          }
        })}

        {/* Broll Layer (non-split overlays only) */}
        <BrollLayerContainer
          brolls={memoizedBrolls.filter((broll) => {
            const layoutValue = String(broll.brollLayout);
            return layoutValue !== 'split-50-50' && layoutValue !== 'split-65-35';
          })}
          videoMetadata={videoMetadata}
          currentFrame={frame}
          isRender={isRender}
          cdnBaseUrlPath={cdnBaseUrlPath}
          mainVideoUrl={src}
        />

        {/* Captions Sound Effects */}
        {captions
          .filter((sub) => sub.soundEffectUrl)
          .map((res, index) => (
            <Sequence
              key={`audio-section-${index}`}
              from={res.startTime * fps}
              durationInFrames={(res.endTime - res.startTime) * fps}
            >
              <Audio
                src={
                  isRender
                    ? `${cdnBaseUrlPath}/assets/sound-effects/${res.soundEffectUrl}`
                    : staticFile(`/assets/sound-effects/${res.soundEffectUrl}`)
                }
                volume={res.soundEffectVolume ? res.soundEffectVolume / 100 : 0}
              />
            </Sequence>
          ))}
        {/*  */}

        {/*B-rolls Sound Effects */}
        {brolls
          .filter((broll) => broll.soundEffectUrl)
          .map((res, index) => (
            <Sequence
              key={`audio-section-${index}`}
              from={res.startTime * fps}
              durationInFrames={(res.endTime - res.startTime) * fps}
            >
              <Audio
                src={
                  isRender
                    ? `${cdnBaseUrlPath}/assets/sound-effects/${res.soundEffectUrl}`
                    : staticFile(`/assets/sound-effects/${res.soundEffectUrl}`)
                }
                volume={res.soundEffectVolume ? res.soundEffectVolume / 100 : 0}
              />
            </Sequence>
          ))}
        {/*  */}

        {/* Caption Layer */}
        <CaptionLayerContainer
          currentFrame={frame}
          theme={theme}
          captions={captions}
          emojiMap={emojiMap}
        />

        {/* Background Audio */}
        {backgroundMusicData?.isBackgroundMusic && backgroundMusicData?.backgroundMusicFile && (
          <Audio
            src={backgroundMusicData.backgroundMusicFile}
            volume={
              backgroundMusicData.backgroundMusicVolume
                ? backgroundMusicData.backgroundMusicVolume / 100
                : 0
            }
            loop={backgroundMusicData.loopIn}
          />
        )}
        {error && (
          <AbsoluteFill
            style={{
              background: 'white',
              color: '#000',
              fontSize: desiredFontSize + 'px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <p> {FALLBACK_VIDEO_MESSAGE} </p>
          </AbsoluteFill>
        )}

        {/* Watermark */}
        {shouldShowWatermark && (
          <AbsoluteFill>
            <img
              src={
                isRender
                  ? `${cdnBaseUrlPath}/assets/watermarkLogo.png`
                  : staticFile(`/assets/watermarkLogo.png`)
              }
              alt="Watermark"
              style={{
                width: width / 4,
                position: 'absolute',
                bottom: '20px',
                right: '20px',
              }}
            />
          </AbsoluteFill>
        )}
      </AbsoluteFill>
    );
  }
);
